import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { ErrorIcon } from '@/assets/icons/error';
import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useActiveProductName } from '@/hooks/use-active-product-name';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { TrustlyFundsGuidanceNavigationProp } from '@/navigation/deposit/types';
import { popToHome } from '@/navigation/navigation';
import { DepositStackParamList } from '@/navigation/types';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useDepositStatusStore } from '../hooks/use-deposit-status-store';

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'TrustlyFundsGuidance'>;

export const TrustlyFundsGuidance = ({ route }: ScreenProps) => {
    const { suggestedRetryAmount } = route.params || {};
    const navigation = useNavigation<TrustlyFundsGuidanceNavigationProp>();

    const rootNavigation = useRootNavigation();

    const { t } = useTranslation(['common', 'transactions']);
    const activeProductName = useActiveProductName();
    const retryAmount = useDepositStatusStore(state => state.retryAmount);
    const TRUSTLY_AFG_SUPPORT_LINK = 'https://www.trustly.com/us/help-center/slp-faqs?audience=personal';

    usePreventAndroidBackNavigation();

    const handlePrimaryButtonPress = () => {
        navigation.popTo('DepositScreen', {
            suggestedRetryAmount: suggestedRetryAmount,
        });
    };

    const handlePress = () => {
        navigation.navigate('ModalWebView', { uri: TRUSTLY_AFG_SUPPORT_LINK });
    };

    const handleCancelPress = () => {
        navigation.popTo('DepositScreen', { suggestedRetryAmount: retryAmount });
    };

    const handleClose = () => {
        popToHome(rootNavigation, 'account');
    };

    return (
        <Screen edges={['top', 'bottom']}>
            <ScreenNavBar closeIconMode="close" onClose={handleClose} />
            <MaxWidthWrapper flex={1}>
                <Box flex={1} justifyContent="space-between" paddingHorizontal="s16">
                    <Box flex={1} justifyContent="center" alignItems="center">
                        <ErrorIcon />
                        <Text textAlign="center" variant="headlineMedium" mt="s16">
                            {t('oops')}
                        </Text>
                        <Text textAlign="center" color={ds.colorToken.text.secondary} variant="bodyMedium" mt="s8">
                            <Trans
                                i18nKey="trustly_available_funds_guidance"
                                ns="transactions"
                                values={{ suggestedRetryAmount: toLocaleCurrency(suggestedRetryAmount) }}
                            >
                                The deposit couldn't be completed due to insufficient funds. Continue with{' '}
                                {toLocaleCurrency(suggestedRetryAmount)} or less. For questions, visit the
                                <TouchableOpacity onPress={handlePress}>
                                    <Text textDecorationLine="underline" lineHeight={19}>
                                        Trustly Help Center
                                    </Text>
                                </TouchableOpacity>
                                .
                            </Trans>
                        </Text>
                    </Box>
                    <Button
                        label={t('transactions:deposit_amount_to_product', {
                            amount: toLocaleCurrency(suggestedRetryAmount),
                            product: activeProductName,
                        })}
                        hierarchy={'primary'}
                        onPress={handlePrimaryButtonPress}
                    />
                    <Text mt="s24" mb="s12" textAlign={'center'} onPress={handleCancelPress}>
                        {t('cancel')}
                    </Text>
                </Box>
            </MaxWidthWrapper>
        </Screen>
    );
};
